import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Trash2, FileSpreadsheet, RefreshCw, Clock, HardDrive } from "lucide-react";
import { toast } from "sonner";

export default function ReportsPage() {
  const { axiosAuth } = useAuth();
  const { settings } = useSettings();
  const accent = settings?.primary_color || "#DC2626";
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      const api = axiosAuth();
      const res = await api.get("/reports");
      setReports(res.data);
    } catch { toast.error("Errore caricamento report"); }
    finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const api = axiosAuth();
      await api.post("/reports/generate");
      toast.success("Report Excel generato!");
      fetchReports();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore generazione report"); }
    finally { setGenerating(false); }
  };

  const downloadReport = async (filename) => {
    try {
      const api = axiosAuth();
      const res = await api.get(`/reports/download/${filename}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download avviato");
    } catch { toast.error("Errore download"); }
  };

  const deleteReport = async (filename) => {
    if (!window.confirm("Eliminare questo report?")) return;
    try {
      const api = axiosAuth();
      await api.delete(`/reports/${filename}`);
      toast.success("Report eliminato");
      setReports((prev) => prev.filter((r) => r.filename !== filename));
    } catch { toast.error("Errore eliminazione"); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const parseFilenameDate = (filename) => {
    const match = filename.match(/riepilogo_(\d{4}-\d{2}-\d{2})_(\d{4})/);
    if (match) {
      const [, date, time] = match;
      const [y, m, d] = date.split("-");
      return `${d}/${m}/${y} ${time.slice(0, 2)}:${time.slice(2)}`;
    }
    return filename;
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-outfit font-bold text-slate-900 dark:text-white">Report Excel</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Riepilogo automatico giornaliero alle 03:00 - noleggi attivi e prenotazioni
          </p>
        </div>
        <Button
          onClick={generateReport}
          disabled={generating}
          data-testid="generate-report-button"
          style={{ backgroundColor: accent }}
          className="hover:opacity-90 text-white rounded-lg shadow-sm"
        >
          {generating ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileSpreadsheet className="w-4 h-4 mr-2" />
          )}
          {generating ? "Generazione..." : "Genera Ora"}
        </Button>
      </div>

      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Report Disponibili
            </p>
            <Badge variant="outline" className="text-xs">
              {reports.length} file
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12 px-4" data-testid="no-reports-message">
              <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <p className="text-sm text-slate-500 dark:text-slate-400">Nessun report disponibile</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                Clicca "Genera Ora" oppure attendi la generazione automatica alle 03:00
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {reports.map((r) => (
                <div
                  key={r.filename}
                  className="flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  data-testid={`report-row-${r.filename}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                    <FileSpreadsheet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                      {parseFilenameDate(r.filename)}
                    </p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(r.created_at)}
                      </span>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <HardDrive className="w-3 h-3" />
                        {formatSize(r.size)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => downloadReport(r.filename)}
                      className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                      data-testid={`download-report-${r.filename}`}
                      title="Scarica"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteReport(r.filename)}
                      className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                      data-testid={`delete-report-${r.filename}`}
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Come funziona</h3>
          <ul className="space-y-1.5 text-xs text-slate-500 dark:text-slate-400">
            <li className="flex items-start gap-2">
              <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: accent }} />
              Il report viene generato automaticamente ogni giorno alle <strong>03:00</strong>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: accent }} />
              Contiene 3 fogli: <strong>Noleggi Attivi</strong>, <strong>Prenotazioni</strong> e <strong>Riepilogo</strong>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: accent }} />
              Puoi generare un report manuale in qualsiasi momento con il pulsante "Genera Ora"
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
